import React, { useContext } from 'react';
import { Paper, Typography, Grid, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box } from '@material-ui/core';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { CustomThemeContext } from '../CustomThemeProvider';

interface Metrics {
  totalSales: number;
  totalOrders: number;
  avgOrderValue: number;
}

interface OrderStatsSectionProps {
  loading: boolean;
  metrics: Metrics;
  salesByDate: Record<string, number>;
  ordersCountByDate: Record<string, number>;
  coversDist?: Record<string, number>;
  quickSale?: number;
  dineIn?: number;
  orders: any[];
  tableColumns: { key: string; label: string; render?: (order: any, idx: number) => React.ReactNode }[];
  classes: any;
  chartTitles?: {
    salesOverTime?: string;
    ordersCountByDate?: string;
    ordersByCovers?: string;
    quickSaleVsDineIn?: string;
    ordersTable?: string;
  };
}

const OrderStatsSection: React.FC<OrderStatsSectionProps> = ({
  loading,
  metrics,
  salesByDate,
  ordersCountByDate,
  coversDist,
  quickSale,
  dineIn,
  orders,
  tableColumns,
  classes,
  chartTitles = {},
}) => {
  const { darkMode } = useContext(CustomThemeContext)!;

  const textColor = darkMode ? '#FFFFFF' : '#000000';
  const gridColor = darkMode ? '#404040' : '#f0f0f0';

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 14,
            weight: '600' as const,
          },
          padding: 20,
          color: textColor,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 13,
          },
          color: textColor,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: gridColor,
        },
        ticks: {
          font: {
            size: 13,
          },
          color: textColor,
        },
      },
    },
  };

  const lineChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      legend: {
        ...commonChartOptions.plugins.legend,
        display: false,
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
      line: {
        tension: 0.3,
      },
    },
  };

  const barChartOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      legend: {
        ...commonChartOptions.plugins.legend,
        display: false,
      },
    },
    elements: {
      bar: {
        borderRadius: 4,
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          font: {
            size: 14,
            weight: '600' as const,
          },
          padding: 15,
          color: textColor,
        },
      },
    },
  };

  return (
    <>
      <Grid container spacing={3} className={classes.marginBottom}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} className={classes.metricCard}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Total Sales
            </Typography>
            <Typography variant="h4">{loading ? <CircularProgress size={24} /> : `£${(metrics.totalSales / 100).toFixed(2)}`}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} className={classes.metricCard}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Total Orders
            </Typography>
            <Typography variant="h4">{loading ? <CircularProgress size={24} /> : metrics.totalOrders}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} className={classes.metricCard}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Avg. Order Value
            </Typography>
            <Typography variant="h4">{loading ? <CircularProgress size={24} /> : `£${(metrics.avgOrderValue / 100).toFixed(2)}`}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.marginBottom}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} className={classes.chartContainer}>
            <Typography variant="h6" className={classes.chartTitle}>
              {chartTitles.salesOverTime || 'Sales Over Time'}
            </Typography>
            <div className={classes.chart}>
              <Line
                data={{
                  labels: Object.keys(salesByDate),
                  datasets: [
                    {
                      label: 'Sales by Date',
                      data: Object.values(salesByDate),
                      borderColor: '#97C031',
                      backgroundColor: 'rgba(151, 192, 49, 0.1)',
                      borderWidth: 3,
                      fill: true,
                    },
                  ],
                }}
                options={lineChartOptions}
              />
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} className={classes.chartContainer}>
            <Typography variant="h6" className={classes.chartTitle}>
              {chartTitles.ordersCountByDate || 'Orders Count by Date'}
            </Typography>
            <div className={classes.chart}>
              <Bar
                data={{
                  labels: Object.keys(ordersCountByDate),
                  datasets: [
                    {
                      label: 'Orders Count by Date',
                      data: Object.values(ordersCountByDate),
                      backgroundColor: '#EF8E24',
                      borderColor: '#EF8E24',
                      borderWidth: 1,
                    },
                  ],
                }}
                options={barChartOptions}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>

      {coversDist && quickSale !== undefined && dineIn !== undefined && (
        <Grid container spacing={3} className={classes.marginBottom}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} className={classes.chartContainer}>
              <Typography variant="h6" className={classes.chartTitle}>
                {chartTitles.ordersByCovers || 'Orders by Covers'}
              </Typography>
              <div className={classes.chart}>
                <Bar
                  data={{
                    labels: Object.keys(coversDist),
                    datasets: [
                      {
                        label: 'Orders by Covers',
                        data: Object.values(coversDist),
                        backgroundColor: '#97C031',
                        borderColor: '#97C031',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={barChartOptions}
                />
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} className={classes.chartContainer}>
              <Typography variant="h6" className={classes.chartTitle}>
                {chartTitles.quickSaleVsDineIn || 'Quick Sale vs Dine-in'}
              </Typography>
              <div className={classes.chart} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Pie
                  data={{
                    labels: ['Quick Sale', 'Dine-in'],
                    datasets: [
                      {
                        data: [quickSale, dineIn],
                        backgroundColor: ['#97C031', '#EF8E24'],
                        borderColor: ['#97C031', '#EF8E24'],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={pieChartOptions}
                />
              </div>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Box mt={6}>
        <Paper elevation={0} className={classes.chartContainer}>
          <Typography variant="h6" className={classes.chartTitle}>
            {chartTitles.ordersTable || 'Recent Orders'}
          </Typography>
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {tableColumns.map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.slice(0, 10).map((order, idx) => (
                  <TableRow key={order.id || idx} hover>
                    {tableColumns.map((col) => (
                      <TableCell key={col.key}>{col.render ? col.render(order, idx) : order[col.key]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </>
  );
};

export default OrderStatsSection;
